import { useState } from 'react';
import { Save, Plus, X, Brain, Sparkles, MessageSquare, Search } from 'lucide-react';
import { Button, Card, Spinner } from '@centraoneplus/ui';
import { pipelineService } from '@/services/pipeline.service';
import type {
  Pipeline,
  PipelineAIConfig,
  AIRoutingConfig,
  AIResolutionConfig,
  AICompletenessConfig,
} from '@/types/pipeline';
import RoutingRulesSection from './RoutingRulesSection';
import ResolutionRoutingSection from './ResolutionRoutingSection';
import CompletenessConfigSection from './CompletenessConfigSection';
import { ThemedSelect } from '@components/common/ThemedSelect';

interface AITabProps {
  pipeline: Pipeline;
  onUpdate: (updatedPipeline: Pipeline) => void;
}

const defaultAIConfig: PipelineAIConfig = {
  enabled: false,
  intentDetection: {
    enabled: true,
    confidenceThreshold: 0.7,
    customIntents: [],
  },
  responseGeneration: {
    enabled: true,
    tone: 'professional',
    autoSuggest: true,
    maxSuggestions: 3,
  },
  sentimentAnalysis: { enabled: true },
  fieldExtraction: { enabled: false, fields: [] },
  // AI-First defaults
  routing: { selectionRules: [], emailDomains: [], emailPrefixes: [] },
  resolution: {
    intentWorkflowMappings: [],
    ticketTypeWorkflowMappings: [],
    availableMcpTools: [],
    confidenceThreshold: 0.6,
    sentimentHandoffThreshold: -0.5,
  },
  completeness: {
    enabled: false,
    maxClarificationAttempts: 3,
    globalRequiredFields: [],
    intentRequirements: {},
  },
};

export default function AITab({ pipeline, onUpdate }: AITabProps) {
  // Merge with defaults to ensure all nested objects exist
  const [config, setConfig] = useState<PipelineAIConfig>(() => {
    const raw = pipeline.aiConfig;
    return {
      enabled: raw?.enabled ?? defaultAIConfig.enabled,
      intentDetection: {
        enabled: raw?.intentDetection?.enabled ?? defaultAIConfig.intentDetection.enabled,
        confidenceThreshold: raw?.intentDetection?.confidenceThreshold ?? defaultAIConfig.intentDetection.confidenceThreshold,
        customIntents: raw?.intentDetection?.customIntents || [],
        allowedIntents: raw?.intentDetection?.allowedIntents || [],
      },
      responseGeneration: {
        enabled: raw?.responseGeneration?.enabled ?? defaultAIConfig.responseGeneration.enabled,
        tone: raw?.responseGeneration?.tone ?? defaultAIConfig.responseGeneration.tone,
        autoSuggest: raw?.responseGeneration?.autoSuggest ?? defaultAIConfig.responseGeneration.autoSuggest,
        maxSuggestions: raw?.responseGeneration?.maxSuggestions ?? defaultAIConfig.responseGeneration.maxSuggestions,
      },
      sentimentAnalysis: {
        enabled: raw?.sentimentAnalysis?.enabled ?? defaultAIConfig.sentimentAnalysis.enabled,
      },
      fieldExtraction: {
        enabled: raw?.fieldExtraction?.enabled ?? defaultAIConfig.fieldExtraction.enabled,
        fields: raw?.fieldExtraction?.fields || [],
      },
      // AI-First configuration
      routing: raw?.routing ?? defaultAIConfig.routing,
      resolution: raw?.resolution ?? defaultAIConfig.resolution,
      completeness: raw?.completeness ?? defaultAIConfig.completeness,
    };
  });
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [newIntent, setNewIntent] = useState({ name: '', keywords: '' });

  // Mock data for workflows and teams (in production, fetch from API)
  const [workflows] = useState<{ id: string; name: string }[]>([
    { id: 'wf-refund', name: 'Refund Processing Workflow' },
    { id: 'wf-order-track', name: 'Order Tracking Workflow' },
    { id: 'wf-account-update', name: 'Account Update Workflow' },
    { id: 'wf-escalation', name: 'Escalation Workflow' },
  ]);

  const [teams] = useState<{ id: string; name: string }[]>([
    { id: 'team-support', name: 'Support Team' },
    { id: 'team-billing', name: 'Billing Team' },
    { id: 'team-technical', name: 'Technical Team' },
    { id: 'team-vip', name: 'VIP Support Team' },
  ]);

  const handleChange = (path: string, value: unknown) => {
    const keys = path.split('.');
    const newConfig = { ...config };
    let current: any = newConfig;

    for (let i = 0; i < keys.length - 1; i++) {
      current[keys[i]] = { ...current[keys[i]] };
      current = current[keys[i]];
    }

    current[keys[keys.length - 1]] = value;
    setConfig(newConfig);
    setHasChanges(true);
  };

  const handleAddIntent = () => {
    if (!newIntent.name.trim()) return;

    const intent = {
      name: newIntent.name,
      keywords: newIntent.keywords.split(',').map((k) => k.trim()).filter(Boolean),
    };

    handleChange('intentDetection.customIntents', [
      ...config.intentDetection.customIntents,
      intent,
    ]);
    setNewIntent({ name: '', keywords: '' });
  };

  const handleRemoveIntent = (index: number) => {
    handleChange(
      'intentDetection.customIntents',
      config.intentDetection.customIntents.filter((_, i) => i !== index)
    );
  };

  // AI-First section handlers
  const handleRoutingChange = (routing: AIRoutingConfig) => {
    setConfig({ ...config, routing });
    setHasChanges(true);
  };

  const handleResolutionChange = (resolution: AIResolutionConfig) => {
    setConfig({ ...config, resolution });
    setHasChanges(true);
  };

  const handleCompletenessChange = (completeness: AICompletenessConfig) => {
    setConfig({ ...config, completeness });
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setError(null);
      await pipelineService.updateAIConfig(pipeline.id, config);
      onUpdate({ ...pipeline, aiConfig: config });
      setHasChanges(false);
    } catch (err) {
      console.error('Error saving AI config:', err);
      setError('Failed to save AI configuration');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Master Toggle */}
      <Card className="p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Brain className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">AI Features</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Enable AI-powered assistance for this pipeline
              </p>
            </div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={config.enabled}
              onChange={(e) => handleChange('enabled', e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600 dark:bg-gray-700"></div>
          </label>
        </div>
      </Card>

      {config.enabled && (
        <>
          {/* Intent Detection */}
          <Card className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <Search className="h-5 w-5 text-blue-500" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Intent Detection</h3>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-700 dark:text-gray-200">Enable Intent Detection</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Automatically classify incoming messages by intent
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={config.intentDetection.enabled}
                    onChange={(e) => handleChange('intentDetection.enabled', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600 dark:bg-gray-700"></div>
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">
                  Confidence Threshold
                </label>
                <div className="flex items-center gap-4">
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={config.intentDetection.confidenceThreshold * 100}
                    onChange={(e) =>
                      handleChange('intentDetection.confidenceThreshold', parseInt(e.target.value) / 100)
                    }
                    className="flex-1"
                  />
                  <span className="text-sm font-medium w-12">
                    {Math.round(config.intentDetection.confidenceThreshold * 100)}%
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1 dark:text-gray-400">
                  Minimum confidence required to classify an intent
                </p>
              </div>

              {/* Custom Intents */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-gray-200">
                  Custom Intents
                </label>
                <div className="space-y-2 mb-3">
                  {config.intentDetection.customIntents.map((intent, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg dark:bg-gray-900"
                    >
                      <span className="font-medium text-gray-900 dark:text-gray-100">{intent.name}</span>
                      <span className="text-gray-400 dark:text-gray-500">-</span>
                      <span className="text-sm text-gray-600 flex-1 truncate dark:text-gray-400">
                        {intent.keywords.join(', ')}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveIntent(index)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newIntent.name}
                    onChange={(e) => setNewIntent({ ...newIntent, name: e.target.value })}
                    placeholder="Intent name"
                    className="w-1/3 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-gray-900 dark:border-gray-700 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
                  />
                  <input
                    type="text"
                    value={newIntent.keywords}
                    onChange={(e) => setNewIntent({ ...newIntent, keywords: e.target.value })}
                    placeholder="Keywords (comma-separated)"
                    className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-gray-900 dark:border-gray-700 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
                  />
                  <Button variant="outline" size="sm" onClick={handleAddIntent}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          </Card>

          {/* Response Generation */}
          <Card className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <MessageSquare className="h-5 w-5 text-green-500" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Response Generation</h3>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-700 dark:text-gray-200">Enable Response Suggestions</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Generate AI-powered response suggestions for agents
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={config.responseGeneration.enabled}
                    onChange={(e) => handleChange('responseGeneration.enabled', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600 dark:bg-gray-700"></div>
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">
                  Response Tone
                </label>
                <ThemedSelect
                  value={config.responseGeneration.tone}
                  onChange={(e) => handleChange('responseGeneration.tone', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-gray-900 dark:border-gray-700 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
                >
                  <option value="professional">Professional</option>
                  <option value="friendly">Friendly</option>
                  <option value="casual">Casual</option>
                </ThemedSelect>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-700 dark:text-gray-200">Auto-Suggest</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Automatically show suggestions when viewing tickets
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={config.responseGeneration.autoSuggest}
                    onChange={(e) => handleChange('responseGeneration.autoSuggest', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600 dark:bg-gray-700"></div>
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">
                  Max Suggestions
                </label>
                <input
                  type="number"
                  value={config.responseGeneration.maxSuggestions}
                  onChange={(e) =>
                    handleChange('responseGeneration.maxSuggestions', parseInt(e.target.value) || 3)
                  }
                  min={1}
                  max={10}
                  className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-gray-900 dark:border-gray-700 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
                />
              </div>
            </div>
          </Card>

          {/* Sentiment Analysis */}
          <Card className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <Sparkles className="h-5 w-5 text-yellow-500" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Sentiment Analysis</h3>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-700 dark:text-gray-200">Enable Sentiment Analysis</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Analyze customer sentiment in messages
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.sentimentAnalysis.enabled}
                  onChange={(e) => handleChange('sentimentAnalysis.enabled', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600 dark:bg-gray-700"></div>
              </label>
            </div>
          </Card>

          {/* AI-First: Pipeline Selection Rules */}
          <RoutingRulesSection
            config={config.routing}
            onChange={handleRoutingChange}
          />

          {/* AI-First: Resolution Routing */}
          <ResolutionRoutingSection
            config={config.resolution}
            workflows={workflows}
            teams={teams}
            onChange={handleResolutionChange}
          />

          {/* AI-First: Completeness Validation */}
          <CompletenessConfigSection
            config={config.completeness}
            onChange={handleCompletenessChange}
          />
        </>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {hasChanges && (
        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? (
              <Spinner size="sm" />
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save AI Configuration
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
}
